

import React, { Suspense, useEffect } from 'react';
import axios from 'axios';
import { Provider } from 'react-redux';
import { persistor, store } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import DynamicRouter from './containers/DynamicRouter';
import SignatureGate from './components/General/SignatureGate';
import Layout from './hoc/Layout/Layout';
import CacheBuster from './CacheBuster';
import { jwtDecode } from 'jwt-decode';
import { setProfileTimezone } from './components/UI/DateDisplay/dates';
import setAuthToken from './setAuthToken';
import { setCurrentUser, logoutUser, setUserProfile } from './actions/authentication';
import { setScreenLock, setBlurScreen } from './actions/screenLock';
import { DeviceHelper } from './helpers/deviceHelper';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { faCheckSquare, faCoffee, faCaretDown, faCaretUp, faSearch, faCaretRight, faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import { faFile, faFileImage, faFilePdf } from '@fortawesome/free-regular-svg-icons';
import ErrorBoundary from './hoc/ErrorBoundary/ErrorBoundary';
import SuspenseLoader from './components/UI/Suspense/Suspense';

import './App.css';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './queryClient';
// Side-effect import: registers the JoyRide tour axios interceptor and the
// store subscription that invalidates React Query on tour-screen changes.
// Must come before any code that issues axios requests at module load.
import './helpers/tourInterceptor';
// Side-effect import: registers per-screen tour mock datasets (cultivation
// hub, schedule, etc.) so the interceptor has something to serve when a
// hub-style tour starts. Detail-screen training data is registered
// elsewhere via training/index.js.
import './training/hubMocks';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Date pickers send instants, and which day an instant names depends on the browser's
// timezone. The server needs it to post inventory on the day the user picked — see
// sopkoa/helpers/requestTimezone.js.
try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) axios.defaults.headers.common['X-Timezone'] = timeZone;
} catch {
    // No Intl timezone support: the server falls back to the user's membership timezone.
}

// Stored dates are shown in the profile timezone (UI/DateDisplay/dates). setCurrentUser keeps
// it current, but on a reload that only runs in App's effect, after the first render; seed it
// from the session token so nothing renders in the browser's zone first.
try {
    if (sessionStorage.jwtToken) setProfileTimezone(jwtDecode(sessionStorage.jwtToken).timezone);
} catch {
    // Unreadable token: the effect below logs the user out or replaces it.
}

axios.interceptors.response.use(undefined, function axiosRetryInterceptor(err) {
    if (err.response && (err.response.status === 401 || err.response.message === '401 Unauthorized') && window.location.hash !== '#/login') {
        store.dispatch(logoutUser());
        window.location.href = '/#/login';
    }
    if (err.response && err.response.status === 409) {
        const body = err.response.data;
        const msg = typeof body === 'string' && body.length > 0
            ? body
            : 'This record was changed by someone else since you opened it. Please refresh your browser and try again.';
        store.dispatch({ type: 'CREATE_ALERT', payload: {
            message: msg,
            title: 'Stale Record',
            severity: 'warning'
        }});
    }
    return Promise.reject(err);
});

function App() {

    useEffect(() => {
        library.add(fab, faCheckSquare, faCoffee, faCaretDown, faCaretUp,
            faSearch, faCaretRight, faBoxOpen, faFile, faFileImage, faFilePdf);
        if (sessionStorage.jwtToken) {
            setAuthToken(sessionStorage.jwtToken);
            const decoded = jwtDecode(sessionStorage.jwtToken);
            store.dispatch(setCurrentUser(decoded));
            const profileObj = sessionStorage.getItem('profile');
            var profile = {};
            if (profileObj != null) {
                profile = JSON.parse(profileObj);
                store.dispatch(setUserProfile(profile));
            }
        }
        if (sessionStorage.screenLock != null) {
            store.dispatch(setScreenLock(sessionStorage.screenLock));
            store.dispatch(setBlurScreen(sessionStorage.screenLock));
        }
        // Reconnect SDK devices on rehydration — sessionStorage loses _sdkDevice prototype methods
        if (sessionStorage.jwtToken) {
            const devicesState = store.getState()?.devices;
            if (devicesState?.hasRegisteredDevices) {
                DeviceHelper.getDevices().then(printers => {
                    store.dispatch({ type: 'SET_ZEBRA_PRINTERS', payload: { printers } });
                }).catch(() => {});
            }
        }
    }, []);

    return (
        <Suspense fallback={<SuspenseLoader />}>
            <CacheBuster>
                {({ loading }) => {
                    // CacheBuster reloads on its own when the bundle is out of date;
                    // loading stays true through that, so there is nothing to do here.
                    if (loading) return null;
                    return (
                        <div>
                            <ErrorBoundary>
                            <QueryClientProvider client={queryClient}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Provider store={store}>
                                        <PersistGate loading={null} persistor={persistor}>
                                            <Layout>
                                                <DynamicRouter />
                                                <SignatureGate />
                                            </Layout>
                                        </PersistGate>
                                    </Provider>
                                </LocalizationProvider>
                            </QueryClientProvider>
                            </ErrorBoundary>
                        </div>
                    );
                }}
            </CacheBuster>
        </Suspense>
    );
}

export default App;